(() => {
  const SNAP = "ww:snap";
  const reducedMotion = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  // How far into the opening scene the headline (and the video behind it) stays up.
  const HERO_END = 0.05;

  /* ---------- Header: clear over the opening photograph, solid after it ---------- */

  function initHeader() {
    const header = document.querySelector(".header");
    if (!header) return;
    const themeColor = document.querySelector('meta[name="theme-color"]');
    const update = () => {
      const stage = document.querySelector("[data-scene-stage]");
      const over = !!stage && stage.getBoundingClientRect().bottom > 72;
      header.dataset.clear = String(over);
      // Phone browsers tint their bars with the page colour: dark while the
      // photo is up, green otherwise.
      document.documentElement.dataset.overPhoto = String(over);
      if (themeColor) themeColor.content = over ? "#120a06" : "#17432f";

      // Mark the link for whichever home-page section is on screen.
      const line = window.innerHeight * 0.4;
      header.querySelectorAll("[data-section-link]").forEach((link) => {
        const target = document.getElementById(link.dataset.sectionLink);
        const rect = target && target.getBoundingClientRect();
        if (rect && rect.top <= line && rect.bottom > line) link.setAttribute("aria-current", "true");
        else link.removeAttribute("aria-current");
      });
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    window.addEventListener(SNAP, update);
  }

  /* ---------- Opening scene: the camera moves through one photograph ---------- */

  function initScene(scene) {
    const stage = scene.querySelector("[data-scene-stage]");
    const cam = scene.querySelector(".scene__cam");
    const shade = scene.querySelector(".scene__shade");
    const hero = scene.querySelector(".scene__hero");
    const notes = scene.querySelectorAll(".scene__note");
    if (!stage || !cam || reducedMotion()) return;

    const ratio = parseFloat(scene.dataset.ratio) || 1.5;
    const point = (value) => {
      const [fx, fy, span] = value.split(",").map((n) => parseFloat(n) / 100);
      return { fx, fy, span };
    };
    const CHEST = point(scene.dataset.first);
    const HOOD = point(scene.dataset.second);
    const narrow = window.matchMedia("(max-width: 820px)");
    const REST = { s: 1, x: 0, y: 0 };
    let chest = REST;
    let hood = REST;

    const ease = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const mix = (a, b, t) => ({
      s: a.s + (b.s - a.s) * t,
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
    });

    function measure() {
      const vw = stage.clientWidth;
      const vh = stage.clientHeight;
      const css = getComputedStyle(scene);
      const px = parseFloat(css.getPropertyValue("--px")) / 100;
      const py = parseFloat(css.getPropertyValue("--py")) / 100;
      // The photo is laid out with object-fit: cover; work out where it sits.
      const iw = vw / vh > ratio ? vw : vh * ratio;
      const ih = iw / ratio;
      const ox = (vw - iw) * px;
      const oy = (vh - ih) * py;

      const pose = (f, fill, at, min, max) => {
        const s = clamp((fill * vw) / (f.span * iw), min, max);
        // Keep the photo covering the frame: never pan past its edges.
        return {
          s,
          x: clamp(at[0] * vw - s * (ox + f.fx * iw), vw * (1 - s), 0),
          y: clamp(at[1] * vh - s * (oy + f.fy * ih), vh * (1 - s), 0),
        };
      };

      if (narrow.matches) {
        chest = pose(CHEST, 0.94, [0.5, 0.36], 1.3, 2);
        hood = pose(HOOD, 1.1, [0.5, 0.3], 1.2, 1.8);
      } else {
        chest = pose(CHEST, 0.46, [0.67, 0.5], 1.6, 2.5);
        hood = pose(HOOD, 0.5, [0.64, 0.44], 1.4, 1.9);
        addSharpLayer();
      }
    }

    // Full-resolution copy so the close-ups stay crisp on large screens.
    function addSharpLayer() {
      const src = scene.dataset.sharp;
      if (!src || cam.querySelector("[data-sharp]")) return;
      const img = new Image();
      img.alt = "";
      img.decoding = "async";
      img.dataset.sharp = "";
      img.style.opacity = "0";
      img.style.transition = "opacity 400ms ease-out";
      img.onload = () => (img.style.opacity = "1");
      img.src = src;
      cam.appendChild(img);
    }

    /* The drop video plays over the photograph before any scrolling. */
    const video = scene.querySelector("[data-scene-video]");
    let videoReady = false;

    function initVideo() {
      if (!video) return;
      const saveData = navigator.connection && navigator.connection.saveData;
      if (saveData) return;

      // Shopify makes several sizes of each video; take the smallest one that
      // still fills the screen sharply.
      const list = (narrow.matches ? video.dataset.phone : video.dataset.wide) || video.dataset.wide || "";
      const sources = list
        .split(",")
        .filter(Boolean)
        .map((entry) => {
          const cut = entry.indexOf("|");
          return { width: Number(entry.slice(0, cut)), url: entry.slice(cut + 1) };
        })
        .sort((a, b) => a.width - b.width);
      if (!sources.length) return;
      const wanted = Math.min(stage.clientWidth * Math.min(window.devicePixelRatio || 1, 2), 1920);
      const pick = sources.find((source) => source.width >= wanted) || sources[sources.length - 1];

      video.addEventListener("playing", () => {
        videoReady = true;
        video.dataset.on = "true";
      });
      // The bundled film also ships in its original HEVC form, which is
      // sharper and smaller; use it where the browser can play it.
      const hevc = video.dataset.hevc && video.canPlayType('video/mp4; codecs="hvc1"') !== "";
      // Some phones say they can play HEVC and then can't; fall back to the
      // standard file once if that happens.
      if (hevc) {
        video.addEventListener(
          "error",
          () => {
            video.src = pick.url;
            video.play().catch(() => {});
          },
          { once: true },
        );
      }
      video.src = hevc ? video.dataset.hevc : pick.url;
      video.play().catch(() => {});
    }

    // The video holds while the headline is on screen, then fades into the
    // photograph as the camera starts to move. It stops while it can't be seen.
    function syncVideo(progress) {
      if (!video || !videoReady) return;
      const fade = clamp((progress - HERO_END) / 0.09, 0, 1);
      const show = 1 - fade * fade * (3 - 2 * fade);
      video.style.setProperty("--video-show", show.toFixed(3));
      // Once scrolling starts, the fade tracks the scroll position directly
      // instead of easing in as it does on first load.
      video.style.transitionDuration = progress > HERO_END ? "0ms" : "";
      if (show === 0 && !video.paused) video.pause();
      if (show > 0 && video.paused) video.play().catch(() => {});
    }

    function poseAt(p) {
      if (p < HERO_END) return REST;
      if (p < 0.26) return mix(REST, chest, ease((p - HERO_END) / (0.26 - HERO_END)));
      if (p < 0.52) return chest;
      if (p < 0.78) return mix(chest, hood, ease((p - 0.52) / 0.26));
      return hood;
    }

    let target = 0;
    let current = 0;
    let frame = 0;
    let beat = 0;
    const SLACK = 0.012;
    // How quickly the camera catches up with the scroll position. A finger on
    // glass already glides, so touch screens follow it almost directly; a
    // mouse wheel moves in steps and gets more smoothing.
    const FOLLOW = window.matchMedia("(pointer: coarse)").matches ? 0.32 : 0.1;

    function readScroll() {
      const rect = scene.getBoundingClientRect();
      const travel = rect.height - stage.clientHeight;
      target = travel > 0 ? clamp(-rect.top / travel, 0, 1) : 0;
    }

    function render() {
      frame = 0;
      // Ease towards the scroll position so wheel steps don't read as jumps.
      current += (target - current) * FOLLOW;
      if (Math.abs(target - current) < 0.0004) current = target;
      const { s, x, y } = poseAt(current);
      cam.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) scale(${s.toFixed(4)})`;
      // The story caption takes over the moment the headline leaves, so
      // there is no empty stretch between them. Each changeover point has a
      // little slack either side, so text doesn't flicker when the scroll
      // position hovers around it.
      const heroEdge = beat === 0 ? HERO_END + SLACK : HERO_END - SLACK;
      const storyEdge = beat === 1 ? 0.58 + SLACK : 0.58 - SLACK;
      const fabricEdge = beat === 2 ? 0.62 - SLACK : 0.62 + SLACK;
      beat = current < heroEdge ? 0 : current < storyEdge ? 1 : current < fabricEdge ? -1 : 2;
      hero.dataset.on = String(beat === 0);
      shade.dataset.close = String(beat !== 0);
      notes.forEach((note, i) => (note.dataset.on = String(beat === i + 1)));
      syncVideo(current);
      if (current !== target) queue();
    }

    function queue() {
      if (!frame) frame = requestAnimationFrame(render);
    }

    function onScroll() {
      readScroll();
      queue();
    }

    // After an instant jump (a nav link), land in the final framing straight
    // away instead of easing the camera there in view of the visitor.
    function snap() {
      readScroll();
      current = target;
      cancelAnimationFrame(frame);
      render();
    }

    measure();
    initVideo();
    snap();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", () => {
      measure();
      onScroll();
    });
    window.addEventListener(SNAP, snap);
  }

  /* ---------- Links within the page cut to their section with a cross-fade ---------- */

  function initJumps() {
    const root = document.documentElement;

    document.addEventListener("click", (e) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const link = e.target.closest ? e.target.closest("a[href]") : null;
      if (!link || link.target === "_blank") return;

      const url = new URL(link.href);
      // Other pages are handled by the browser's own page transition.
      if (url.origin !== location.origin || url.pathname !== location.pathname) return;
      if (url.search !== location.search) return;

      // No hash means "back to the top of this page".
      const target = url.hash
        ? document.getElementById(decodeURIComponent(url.hash.slice(1)))
        : null;
      if (url.hash && !target) return;

      e.preventDefault();

      const destination = target
        ? target.getBoundingClientRect().top + window.scrollY
        : 0;
      if (!target && window.scrollY < 1) return;

      const jump = () => {
        if (target) {
          target.scrollIntoView({ behavior: "instant", block: "start" });
          target.setAttribute("tabindex", "-1");
          target.focus({ preventScroll: true });
        } else {
          window.scrollTo({ top: 0, behavior: "instant" });
        }
        history.pushState(null, "", url.hash || url.pathname + url.search);
        window.dispatchEvent(new Event(SNAP));
        settle();
      };

      if (reducedMotion() || !document.startViewTransition) {
        jump();
        return;
      }

      // The new view arrives from the direction of travel.
      root.dataset.jump = destination < window.scrollY ? "up" : "down";
      const transition = document.startViewTransition(jump);
      // If the browser skips the animation (hidden tab, a second click), the
      // jump has still happened, so there is nothing to recover.
      transition.ready.catch(() => {});
      transition.finished.finally(() => delete root.dataset.jump);
    });
  }

  /* ---------- Bag drawer ---------- */

  const SECTION = "cart-drawer";

  function initBag() {
    const dialog = document.getElementById("bag");
    if (!dialog) return null;
    const inner = () => dialog.querySelector(".bag__inner");
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

    function syncCount() {
      const count = inner().dataset.count || "0";
      document.querySelectorAll("[data-bag-count]").forEach((el) => {
        if (el.textContent.trim() === count) return;
        el.textContent = count;
        el.dataset.empty = String(count === "0");
        // Restart the pop so the number reacts when something is added.
        el.style.animation = "none";
        void el.offsetWidth;
        el.style.animation = "";
      });
      document.querySelectorAll("[data-bag-status]").forEach((el) => {
        el.textContent = count === "1" ? "1 item in your bag" : `${count} items in your bag`;
      });
    }

    // Shopify sends back the drawer's fresh HTML with each cart change.
    function swap(html) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const next = doc.querySelector(".bag__inner");
      if (!next) return;
      inner().replaceWith(next);
      syncCount();
      applyLaunchLock();
    }

    function showError(text) {
      const el = dialog.querySelector(".bag__error");
      if (el) el.textContent = text;
    }

    async function post(path, body) {
      const res = await fetch(`${root}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ ...body, sections: SECTION, sections_url: location.pathname }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.description || data.message || "That didn't go through. Try again.");
      }
      if (data.sections && data.sections[SECTION]) swap(data.sections[SECTION]);
      return data;
    }

    const open = () => {
      if (!dialog.open) dialog.showModal();
    };
    const close = () => dialog.close();

    dialog.addEventListener("click", async (e) => {
      if (e.target === dialog || e.target.closest("[data-bag-close]")) {
        close();
        return;
      }
      const change = e.target.closest("[data-line]");
      if (!change) return;
      const lines = dialog.querySelector(".bag__lines");
      lines.setAttribute("aria-busy", "true");
      dialog.querySelectorAll("[data-line]").forEach((b) => (b.disabled = true));
      try {
        await post("cart/change.js", {
          id: change.dataset.line,
          quantity: Number(change.dataset.quantity),
        });
      } catch (err) {
        lines.setAttribute("aria-busy", "false");
        dialog.querySelectorAll("[data-line]").forEach((b) => (b.disabled = false));
        showError(err.message);
      }
    });

    document.querySelectorAll("[data-bag-open]").forEach((el) =>
      el.addEventListener("click", (e) => {
        e.preventDefault();
        open();
      }),
    );

    syncCount();
    return { open, add: (id) => post("cart/add.js", { items: [{ id, quantity: 1 }] }) };
  }

  /* ---------- Size picker and add to bag ---------- */

  function initBuyForms(bag) {
    document.querySelectorAll("[data-buy]").forEach((form) => {
      const message = form.querySelector(".buy__message");
      const button = form.querySelector("[type=submit]");
      const label = form.querySelector("[data-buy-label]");

      form.addEventListener("change", () => (message.textContent = ""));

      form.addEventListener("submit", async (e) => {
        if (isLocked()) {
          e.preventDefault();
          return;
        }
        if (!bag) return; // No drawer: let the form post to the cart page.
        e.preventDefault();
        const id = new FormData(form).get("id");
        if (!id) {
          message.textContent = "Choose a size first.";
          return;
        }
        const idle = label.textContent;
        button.disabled = true;
        label.textContent = "Adding to bag…";
        message.textContent = "";
        try {
          await bag.add(Number(id));
          bag.open();
        } catch (err) {
          message.textContent = err.message;
        } finally {
          button.disabled = false;
          label.textContent = idle;
        }
      });
    });
  }

  /* ---------- Product photos: swipe, arrows or thumbnails ---------- */

  function initGallery(gallery) {
    const track = gallery.querySelector(".gallery__track");
    const thumbs = gallery.querySelectorAll("[data-gallery-thumb]");
    const counter = gallery.querySelector("[data-gallery-index]");
    const total = track.children.length;
    if (total < 2) return;
    let index = 0;
    let frame = 0;

    const show = (i) => {
      const next = (i + total) % total;
      track.scrollTo({
        left: next * track.clientWidth,
        behavior: reducedMotion() ? "instant" : "smooth",
      });
    };

    track.addEventListener(
      "scroll",
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          index = Math.round(track.scrollLeft / track.clientWidth);
          if (counter) counter.textContent = String(index + 1);
          thumbs.forEach((thumb, i) => thumb.setAttribute("aria-current", String(i === index)));
        });
      },
      { passive: true },
    );

    thumbs.forEach((thumb, i) => thumb.addEventListener("click", () => show(i)));
    gallery.querySelectorAll("[data-gallery-step]").forEach((button) =>
      button.addEventListener("click", () => show(index + Number(button.dataset.galleryStep))),
    );
  }

  /* ---------- Arriving: reveal sections as they come into view, fade photos in ---------- */

  function initMotion() {
    window.__wwMotion = true;

    const reveals = document.querySelectorAll("[data-reveal]");
    if (document.documentElement.dataset.motion !== "on" || !("IntersectionObserver" in window)) {
      reveals.forEach((el) => (el.dataset.revealed = "now"));
    } else {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            if (!entry.target.dataset.revealed) entry.target.dataset.revealed = "true";
            observer.unobserve(entry.target);
          });
        },
        {
          rootMargin: window.matchMedia("(pointer: coarse)").matches ? "0px 0px -4% 0px" : "0px 0px -12% 0px",
          threshold: 0.05,
        },
      );
      reveals.forEach((el) => observer.observe(el));
    }

    document.querySelectorAll(".gallery__slide img, .gallery__thumb img, .card__photo img").forEach((img) => {
      if (img.complete) return;
      img.dataset.loading = "";
      const done = () => delete img.dataset.loading;
      img.addEventListener("load", done, { once: true });
      img.addEventListener("error", done, { once: true });
    });
  }

  // A section reached by a link is shown already settled, so the page fade
  // isn't followed by a second animation.
  function settle(target) {
    (target || document).querySelectorAll("[data-reveal]:not([data-revealed])").forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) el.dataset.revealed = "now";
    });
  }

  /* ---------- Drop launch: lock ordering until the launch time ---------- */

  const launchAt = Number(document.documentElement.dataset.launch || 0) * 1000;
  const isLocked = () => launchAt > 0 && Date.now() < launchAt;

  // Sets every order control to match the lock. Called on load, whenever the
  // bag is redrawn, and at the moment the drop opens.
  function applyLaunchLock() {
    const locked = isLocked();
    if (launchAt) document.documentElement.dataset.locked = String(locked);
    document.querySelectorAll("[data-buy]").forEach((form) => {
      form.querySelectorAll(".size").forEach((chip) => {
        chip.querySelector("input").disabled = locked || chip.dataset.out === "true";
      });
      const button = form.querySelector("[type=submit]");
      if (button) button.disabled = locked || button.dataset.available !== "true";
    });
    document.querySelectorAll("[data-checkout]").forEach((button) => (button.disabled = locked));
  }

  function initLaunch() {
    applyLaunchLock();
    if (!isLocked()) return;

    const pad = (n) => String(n).padStart(2, "0");
    const tick = () => {
      const left = launchAt - Date.now();
      if (left <= 0) {
        clearInterval(timer);
        applyLaunchLock();
        return;
      }
      const s = Math.floor(left / 1000);
      const days = Math.floor(s / 86400);
      const text =
        (days ? `${days}d ` : "") +
        `${pad(Math.floor((s % 86400) / 3600))}h ${pad(Math.floor((s % 3600) / 60))}m ${pad(s % 60)}s`;
      document.querySelectorAll("[data-countdown]").forEach((el) => (el.textContent = text));
    };
    const timer = setInterval(tick, 1000);
    tick();
  }

  /* ---------- Store policies: a list of sections beside the text ---------- */

  function initPolicy() {
    const container = document.querySelector(".shopify-policy__container");
    const title = container && container.querySelector(".shopify-policy__title");
    const headings = container ? [...container.querySelectorAll(".shopify-policy__body h2")] : [];
    if (!title || headings.length < 3) return;

    const nav = document.createElement("nav");
    nav.className = "policy-toc";
    nav.setAttribute("aria-label", "Sections of this policy");
    const links = headings.map((heading, i) => {
      heading.id = heading.id || `section-${i + 1}`;
      const link = document.createElement("a");
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();
      nav.appendChild(link);
      return link;
    });
    title.appendChild(nav);

    // Mark the section currently being read.
    let frame = 0;
    const mark = () => {
      frame = 0;
      const line = window.innerHeight * 0.3;
      let current = -1;
      headings.forEach((heading, i) => {
        if (heading.getBoundingClientRect().top <= line) current = i;
      });
      links.forEach((link, i) => {
        if (i === current) link.setAttribute("aria-current", "true");
        else link.removeAttribute("aria-current");
      });
    };
    const queue = () => {
      if (!frame) frame = requestAnimationFrame(mark);
    };
    window.addEventListener("scroll", queue, { passive: true });
    window.addEventListener(SNAP, queue);
    mark();
  }

  function init() {
    initLaunch();
    initPolicy();
    initMotion();
    document.querySelectorAll("[data-gallery]").forEach(initGallery);
    document.querySelectorAll("[data-scene]").forEach(initScene);
    initHeader();
    initJumps();
    initBuyForms(initBag());
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
